/**
 * Shared dependencies
 */
import type { StoreFunctions } from '../../shared/types/store';
import type { RecipeListItem } from '../../shared/types/lists';

/**
 * Internal dependencies
 */
import { addItemsToUserList, type AddItemsToUserListParams } from '../requests';

/**
 * Fetches list items for a specific list and updates the store.
 * Also dispatches a ListEvent to notify other parts of the app.
 *
 * @param fn - Function to update the store state.
 * @param listId - The ID of the list to fetch items for.
 * @return Promise that resolves to an array of list items.
 */
export async function listsAddItems(
	fn: StoreFunctions,
	listId: string,
	items: AddItemsToUserListParams['items']
): Promise<RecipeListItem[]> {
	const {
		events: { RecipeEvent },
	} = window.valioApp;

	const response = await addItemsToUserList({ listId, items });

	// Update the items for this specific list in the store
	fn.set((state) => {
		const list = state.data.lists[listId];
		if (!list) {
			return state;
		}

		return {
			data: {
				lists: {
					...state.data.lists,
					[listId]: {
						...list,
						items: [...list.items, ...response],
					},
				},
			},
		};
	});

	RecipeEvent.save({
		listId,
		items: response,
		lastSavedItem: response[response.length - 1],
	});

	return response;
}
